/**
 * src/components/publico/ResumenPlan.jsx
 * La ficha del plan que el visitante acaba de pulsar en la landing, viajando
 * con él hasta el alta.
 *
 * Hasta ahora el alta no decía NI el precio NI el ciclo: quien elegía «Anual»
 * en cmmonitor.cl aterrizaba en un formulario que no se lo confirmaba por
 * ninguna parte, y tenía que fiarse de su memoria justo en el momento de dar
 * sus datos. Esto convierte el trámite en la confirmación de una decisión.
 *
 * Los datos salen de src/data/planes.js — la misma tabla que pinta /pricing.
 */
import { Link } from 'react-router-dom'
import {
  MESES_ANUAL, fmtUF, precioAnual, precioPorMes, tienePrecio,
} from '../../data/planes'

function Tic() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--color-ok)"
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
         style={{ flexShrink: 0, marginTop: 'var(--space-05)' }} aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

const ICONOS_CONFIANZA = {
  tarjeta: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
      <path d="m3 20 18-16" />
    </>
  ),
  reloj: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  candado: (
    <>
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
}

function Confianza({ icono, children }) {
  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-2)"
           strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
           style={{ flexShrink: 0 }} aria-hidden="true">
        {ICONOS_CONFIANZA[icono]}
      </svg>
      <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-2)' }}>{children}</span>
    </div>
  )
}

/** Cuántos rasgos del plan se enseñan aquí. La lista completa está en /pricing. */
const RASGOS_VISIBLES = 3

export function ResumenPlan({ plan, anual, urlCambiar = '/pricing' }) {
  // Sin plan en la URL no hay nada que confirmar, y fingir uno sería peor:
  // se dice lo que va a pasar y se sigue.
  if (!plan) {
    return (
      <div style={{
        background: 'var(--color-surface)', border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)',
        display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
      }}>
        <span style={{ fontFamily: 'var(--font-brand)', fontSize: '17px', fontWeight: 600 }}>
          Todavía sin plan elegido
        </span>
        <p style={{ margin: 0, fontSize: 'var(--text-md)', color: 'var(--color-text-2)', lineHeight: 1.5 }}>
          Crea la cuenta y eliges plan justo después. También puedes{' '}
          <Link to={urlCambiar} style={{ color: 'var(--color-accent-2)', textDecoration: 'none', display: 'inline-block', padding: '12px 2px', margin: '-12px 0', }}>
            verlos ahora
          </Link>.
        </p>
      </div>
    )
  }

  // Sin precio publicado (Enterprise, desde el 31-10-2026) no se pinta NINGUNA
  // cifra: antes salía «desde 8,25 UF/mes» junto a «Precio a medida».
  const conPrecio = tienePrecio(plan)
  const porMes = precioPorMes(plan, anual)

  return (
    <div style={{
      background: 'var(--color-surface)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-lg)', padding: '26px',
      display: 'flex', flexDirection: 'column', gap: '18px',
    }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <span style={{ fontFamily: 'var(--font-brand)', fontSize: 'var(--text-lg)', fontWeight: 600 }}>
          Plan {plan.nombre}
        </span>
        {plan.featured && (
          <span style={{
            fontSize: 'var(--text-xs)', color: 'var(--color-accent-2)',
            border: '1px solid rgba(255, 138, 92, 0.35)', borderRadius: 'var(--radius-pill)',
            padding: '4px 11px', whiteSpace: 'nowrap',
          }}>
            Más elegido
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {conPrecio && <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
          {plan.desde && (
            <span style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-2)' }}>desde</span>
          )}
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--text-3xl)', fontWeight: 500,
            letterSpacing: '-0.02em', lineHeight: 1,
          }}>
            {fmtUF(porMes)}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-lg)', color: 'var(--color-text-2)' }}>
            UF/mes
          </span>
        </div>}
        <p style={{ margin: 0, fontSize: 'var(--text-base)', color: 'var(--color-text-2)', lineHeight: 1.5 }}>
          {!conPrecio
            ? 'Plan a medida, sin precio publicado'
            : anual
              ? `${fmtUF(precioAnual(plan))} UF/año · facturación anual`
              : 'Facturación mensual'}
          {conPrecio ? ' · IVA no incluido · ' : ' · '}
          <Link to={urlCambiar} style={{ color: 'var(--color-accent-2)', textDecoration: 'none', display: 'inline-block', padding: '12px 2px', margin: '-12px 0', }}>
            cambiar plan o ciclo
          </Link>
        </p>
      </div>

      <div style={{ height: '1px', background: 'var(--color-border)' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {plan.features.slice(0, RASGOS_VISIBLES).map(f => (
          <div key={f} style={{ display: 'flex', gap: '11px', alignItems: 'flex-start' }}>
            <Tic />
            <span style={{ fontSize: 'var(--text-md)', lineHeight: 1.45 }}>{f}</span>
          </div>
        ))}
      </div>

      <div style={{ height: '1px', background: 'var(--color-border)' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
        <Confianza icono="tarjeta">No pedimos tarjeta</Confianza>
        <Confianza icono="reloj">Cancelas cuando quieras</Confianza>
        {/* Aquí decía que el precio quedaba congelado dos años, que era el
            Programa Pionero hasta el 28-10-2026 y dejó de serlo: ahora es un 25 % durante 12 meses,
            solo en anual. Y NO se cambia por la frase nueva, a propósito: el
            Pionero es por selección (diez plazas, con algo a cambio) y Flow aún
            no cobra ese 25 %, así que prometérselo a todo el que se da de alta
            sería otra promesa falsa en la pantalla donde se compromete a pagar.
            Lo que sí vale para todos se dice, y solo en el ciclo en que es verdad. */}
        {anual && conPrecio && (
          <Confianza icono="candado">Pagas {MESES_ANUAL} meses y tienes 12</Confianza>
        )}
      </div>
    </div>
  )
}

/**
 * Qué pasa al pulsar el botón. Nadie lo decía, así que el visitante no sabía si
 * le iban a pedir una tarjeta, un Excel o dos horas de configuración.
 * ⚠️ El paso 3 dice «eliges tu familia» a propósito: el onboarding SÍ pide
 * familia y tienda, así que prometer «nada que configurar» sería falso.
 */
export function QuePasaDespues() {
  const pasos = [
    'Creas la cuenta y nos dices tu RUT',
    'Detectamos tu empresa y tus productos en Convenio Marco',
    'Eliges tu familia de Convenio Marco y tu panel queda listo',
  ]
  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)',
      padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '14px',
    }}>
      <span style={{
        fontSize: 'var(--text-sm)', letterSpacing: '0.09em', color: 'var(--color-text-3)',
        textTransform: 'uppercase',
      }}>
        Qué pasa después
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {pasos.map((texto, i) => (
          <div key={texto} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
            <span style={{
              flexShrink: 0, width: '21px', height: '21px', borderRadius: 'var(--radius-pill)',
              background: 'rgba(255, 255, 255, 0.07)', fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-xs)', color: 'var(--color-text-2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {i + 1}
            </span>
            <span style={{ fontSize: 'var(--text-md)', lineHeight: 1.45 }}>{texto}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
